import { Amplify } from 'aws-amplify';
import { signUp, signIn, signInWithRedirect, confirmSignUp, resendSignUpCode, resetPassword, confirmResetPassword, signOut, getCurrentUser, fetchUserAttributes } from 'aws-amplify/auth';

// Configure Amplify
Amplify.configure({
  Auth: {
    Cognito: {
      userPoolId: process.env.REACT_APP_COGNITO_USER_POOL_ID || '',
      userPoolClientId: process.env.REACT_APP_COGNITO_CLIENT_ID || '',
      signUpVerificationMethod: 'code',
      passwordFormat: {
        minLength: 8,
        requireNumbers: true,
        requireSpecialCharacters: true,
        requireUppercase: true,
        requireLowercase: true
      },
      loginWith: {
        email: true,
        phone: false,
        username: false,
        oauth: {
          domain: process.env.REACT_APP_COGNITO_DOMAIN || '',
          scopes: ['email', 'profile', 'openid', 'aws.cognito.signin.user.admin'],
          redirectSignIn: [process.env.REACT_APP_REDIRECT_SIGN_IN || ''],
          redirectSignOut: [process.env.REACT_APP_REDIRECT_SIGN_OUT || ''],
          responseType: 'code',
          providers: ['Google']
        }
      }
    }
  }
});

export interface AuthError {
  code: string;
  message: string;
}

export const handleAuthError = (error: any): AuthError => {
  console.error('Auth error:', error);
  
  if (error.name === 'UserNotConfirmedException') {
    return {
      code: 'UserNotConfirmed',
      message: 'Please verify your email address',
    };
  }
  
  if (error.name === 'UserNotFoundException') {
    return {
      code: 'UserNotFound',
      message: 'User not found',
    };
  }
  
  if (error.name === 'NotAuthorizedException') {
    return {
      code: 'InvalidCredentials',
      message: 'Incorrect email or password',
    };
  }
  
  if (error.name === 'CodeMismatchException') {
    return {
      code: 'InvalidCode',
      message: 'Invalid verification code',
    };
  }
  
  return {
    code: 'UnknownError',
    message: 'An error occurred. Please try again.',
  };
};

export const createUser = async (email: string, password: string) => {
  try {
    const signUpResponse = await signUp({
      username: email,
      password,
      options: {
        userAttributes: {
          email,
        },
      },
    });
    return signUpResponse;
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const verifyUser = async (email: string, code: string) => {
  try {
    const confirmSignUpResponse = await confirmSignUp({
      username: email,
      confirmationCode: code,
    });
    return confirmSignUpResponse;
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const signInUser = async (email: string, password: string) => {
  try {
    // First try to sign out any existing user
    try {
      await signOut();
    } catch (error) {
      // Ignore sign out errors, just proceed with sign in
      console.log('Sign out during sign in:', error);
    }

    console.log('Attempting to sign in user:', email);
    const signInResponse = await signIn({
      username: email,
      password,
      options: {
        authFlowType: 'USER_SRP_AUTH'
      }
    });
    console.log('Sign in response:', signInResponse);
    return signInResponse;
  } catch (error) {
    console.error('Sign in error:', error);
    throw handleAuthError(error);
  }
};

export const signInWithGoogle = async () => {
  try {
    await signInWithRedirect({
      provider: 'Google',
      options: {
        preferPrivateSession: false,
        loginHint: undefined,
        lang: 'en'
      }
    });
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const forgotUserPassword = async (email: string) => {
  try {
    const response = await resetPassword({ username: email });
    return response;
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const confirmUserPasswordReset = async (email: string, code: string, newPassword: string) => {
  try {
    const response = await confirmResetPassword({
      username: email,
      confirmationCode: code,
      newPassword,
    });
    return response;
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const resendVerificationCode = async (email: string) => {
  try {
    const response = await resendSignUpCode({ username: email });
    return response;
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const signOutUser = async () => {
  try {
    await signOut();
  } catch (error) {
    throw handleAuthError(error);
  }
};

export const getCurrentAuthUser = async () => {
  try {
    // First check if user is authenticated
    try {
      const user = await getCurrentUser();
      // If we get here, user is authenticated, so fetch attributes
      try {
        const attributes = await fetchUserAttributes();
        
        if (!attributes.email) {
          console.log('No email found in user attributes');
          return null;
        }

        return {
          ...user,
          username: attributes.email,
          userId: attributes.sub // The sub attribute is the unique identifier (userId) in Cognito
        };
      } catch (attrError: any) {
        console.log('Error fetching user attributes:', attrError);
        // If we can't get attributes but have a user, return user with default values
        return {
          ...user,
          userId: user.userId || user.username // Fallback to username if userId not available
        };
      }
    } catch (error: any) {
      if (error.name === 'UserUnAuthenticatedException' || 
          error.name === 'NotAuthorizedException') {
        console.log('User is not authenticated');
        return null;
      }
      throw error; // Re-throw other errors
    }
  } catch (error) {
    console.log('Error getting current user:', error);
    return null;
  }
}; 